import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Ban, Flag, Undo2, Star, ShoppingBag } from "lucide-react";
import { BadgeChip } from "../lib/games";
import { toast } from "sonner";
import { KidAvatar } from "../components/Brand";
import { VideoCard } from "../components/VideoCard";
import { ReportDialog } from "../components/ReportDialog";
import { kidApi, errMsg } from "../lib/api";

export default function Creator() {
  const { id } = useParams();
  const nav = useNavigate();
  const [c, setC] = useState(null);
  const [report, setReport] = useState(null);
  const load = () => kidApi.get(`/kid/creators/${id}`).then((r) => setC(r.data)).catch(() => setC(false));
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleBlock = async () => {
    try {
      if (c.blocked) await kidApi.delete(`/kid/blocks/${id}`);
      else await kidApi.post(`/kid/blocks/${id}`);
      toast.success(c.blocked ? `${c.name} is unblocked` : `You won't see ${c.name}'s videos anymore`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (c === false) return <p data-testid="creator-not-found" className="font-display text-2xl">Creator not found.</p>;
  if (!c) return <p className="font-display text-xl">Loading…</p>;

  return (
    <div>
      <button data-testid="creator-back-btn" onClick={() => nav(-1)} className="zn-chip bg-white mb-6"><ArrowLeft size={16} /> Back</button>
      <div className="zn-card p-6 sm:p-8 bg-[#FFD23F] flex flex-col sm:flex-row items-center gap-5 mb-10">
        <KidAvatar avatar={c.avatar} color={c.color} frame={c.frame} size={96} />
        <div className="text-center sm:text-left flex-1">
          <h1 data-testid="creator-name" className="text-4xl font-bold">{c.name}</h1>
          <p className="font-semibold flex flex-wrap items-center justify-center sm:justify-start gap-2">{c.videos.length} video{c.videos.length === 1 ? "" : "s"} <span data-testid="creator-stars" className="zn-chip !min-h-0 !py-1 bg-white text-xs"><Star size={13} fill="#1B1A2E" /> {c.stars} stars</span>{c.is_me && <Link to="/kid/shop" data-testid="creator-shop-link" className="zn-chip !min-h-0 !py-1 bg-[#FF8FC7] text-xs"><ShoppingBag size={13} /> Star Shop</Link>}</p>
          {c.badges?.length > 0 && <div data-testid="creator-badges" className="flex flex-wrap justify-center sm:justify-start gap-3 mt-3">{c.badges.map((b) => <BadgeChip key={b} id={b} size="sm" />)}</div>}
        </div>
        {!c.is_me && (
          <div className="flex gap-2">
            <button data-testid="creator-report-btn" onClick={() => setReport({ type: "creator", id })} className="zn-btn bg-white !py-2 !px-4"><Flag size={16} /> Report</button>
            <button data-testid="creator-block-btn" onClick={toggleBlock} className="zn-btn bg-white !py-2 !px-4">{c.blocked ? <><Undo2 size={16} /> Unblock</> : <><Ban size={16} /> Block</>}</button>
          </div>
        )}
      </div>
      {c.blocked ? (
        <p data-testid="creator-blocked-msg" className="zn-card p-8 text-center bg-white">You blocked this creator. Their videos are hidden.</p>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-8">{c.videos.map((v, i) => <VideoCard key={v.id} video={v} index={i} />)}</div>
      )}
      <ReportDialog target={report} onClose={() => setReport(null)} />
    </div>
  );
}
